/**
 * Top-level component for the SAT landing page.
 */

import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {MediaLayout} from "@khanacademy/wonder-blocks-layout";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {Body, BodySerifBlock} from "@khanacademy/wonder-blocks-typography";
import {staticUrl} from "@repo/urls";

type SummaryFeature = {
    key: string;
    image: string;
    imageWidth: string;
    title: string;
    body: string;
};

type Props = {
    features: Array<SummaryFeature>;
};

export default function DemoModule({features}: Props): React.ReactElement {
    return (
        <MediaLayout styleSheets={styleSheets}>
            {({styles}) => {
                return (
                    <View style={styles.wrapper}>
                        {features.map((feature) => (
                            <View style={styles.featureBlock} key={feature.key}>
                                <View style={styles.featureImage}>
                                    <img
                                        alt=""
                                        aria-hidden="true"
                                        src={staticUrl(feature.image)}
                                        width={feature.imageWidth}
                                    />
                                </View>
                                <BodySerifBlock style={styles.featureTitle}>
                                    {feature.title}
                                </BodySerifBlock>
                                <Body style={styles.featureBody}>
                                    {feature.body}
                                </Body>
                            </View>
                        ))}
                    </View>
                );
            }}
        </MediaLayout>
    );
}

const styleSheets = {
    all: StyleSheet.create({
        wrapper: {
            display: "flex",
            justifyContent: "space-between",
            margin: "0 auto",
            maxWidth: 1000,
            paddingLeft: 12,
            paddingRight: 12,
        },
        featureBlock: {
            margin: 12,
        },
        featureBody: {
            color: color.offBlack,
            textAlign: "center",
            lineHeight: "26px",
        },
        featureTitle: {
            color: "black",
            fontFamily: "Source Serif Pro",
            marginTop: 20,
            marginBottom: 20,
            textAlign: "center",
            width: "100%",
        },
        featureImage: {
            justifyContent: "center",
            height: 110,
        },
    }),
    mdOrLarger: StyleSheet.create({
        wrapper: {
            flexDirection: "row",
            paddingTop: 88,
            paddingBottom: 112,
        },
        featureBlock: {
            display: "flex",
            maxWidth: 214,
            alignItems: "center",
            width: "100%",
        },
    }),
    small: StyleSheet.create({
        // Note(nisha): The following styles are used. The linter is confused
        // on the last style so we have to disable eslint.
        // eslint-disable-next-line react-native/no-unused-styles
        wrapper: {
            flexDirection: "column",
            paddingTop: 64,
            paddingBottom: 8,
        },
        // eslint-disable-next-line react-native/no-unused-styles
        featureBlock: {
            display: "flex",
            maxWidth: 260,
            margin: "0 auto",
            marginBottom: 56,
            alignItems: "center",
            width: "100%",
        },
    }),
} as const;
